import {
  Box,
  Divider,
  Heading,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { FC, Fragment, ReactNode, useEffect } from "react";
import { Link, Navigate, useParams } from "react-router";

import { PageSection, setMetaDescription } from "../../components";

import { DELIVERY_TYPES } from "./consts/delivery-types";

// Matches either an inline `<span style="...">label</span>` (used for colored
// category labels and legend swatches) or a `**bold**` run.
const INLINE_REGEX = /(<span style="[^"]*">[^<]*<\/span>|\*\*[^*]+\*\*)/g;

const parseStyle = (style: string): Record<string, string> => {
  const result: Record<string, string> = {};
  style.split(";").forEach((declaration) => {
    const [key, value] = declaration.split(":").map((part) => part?.trim());
    if (key && value) result[key] = value;
  });
  return result;
};

const renderInline = (text: string): ReactNode =>
  text
    .split(INLINE_REGEX)
    .filter((part) => part !== "")
    .map((part, index) => {
      const spanMatch = part.match(/^<span style="([^"]*)">([^<]*)<\/span>$/);
      if (spanMatch) {
        const style = parseStyle(spanMatch[1]);
        const label = spanMatch[2];
        if (label) {
          return (
            <Box as="span" key={index} color={style.color} fontWeight={700}>
              {label}
            </Box>
          );
        }
        return (
          <Box
            as="span"
            key={index}
            display="inline-block"
            w={style.width ?? "0.9em"}
            h={style.height ?? "0.9em"}
            borderRadius={style["border-radius"] ?? "2px"}
            bg={style.background}
            verticalAlign="middle"
          />
        );
      }

      if (part.startsWith("**") && part.endsWith("**")) {
        return (
          <Box as="strong" key={index} fontWeight={700}>
            {part.slice(2, -2)}
          </Box>
        );
      }

      return <Fragment key={index}>{part}</Fragment>;
    });

const parseTableRow = (line: string): string[] =>
  line
    .trim()
    .replace(/^\|/, "")
    .replace(/\|$/, "")
    .split("|")
    .map((cell) => cell.trim());

const isTableSeparatorRow = (line: string): boolean => /^\|?[\s:|-]+$/.test(line.trim());

const DeliveryTypeTable: FC<{ header: string[]; rows: string[][] }> = ({ header, rows }) => (
  <TableContainer mb={6}>
    <Table size="sm" variant="simple">
      <Thead>
        <Tr>
          {header.map((cell, i) => (
            <Th key={i}>{cell}</Th>
          ))}
        </Tr>
      </Thead>
      <Tbody>
        {rows.map((row, i) => (
          <Tr key={i}>
            {row.map((cell, j) => (
              <Td key={j} whiteSpace="normal">
                {renderInline(cell)}
              </Td>
            ))}
          </Tr>
        ))}
      </Tbody>
    </Table>
  </TableContainer>
);

const DeliveryTypeContent: FC<{ content: string }> = ({ content }) => {
  const lines = content.split("\n");
  const blocks: ReactNode[] = [];

  let i = 0;
  while (i < lines.length) {
    const line = lines[i];

    if (line.startsWith("## ")) {
      blocks.push(
        <Heading key={i} as="h2" fontSize="xl" color="text" mt={8} mb={4}>
          {line.replace("## ", "")}
        </Heading>
      );
      i += 1;
      continue;
    }

    if (line.startsWith("---")) {
      blocks.push(<Divider key={i} my={8} />);
      i += 1;
      continue;
    }

    if (line.trim() === "") {
      blocks.push(<Box key={i} h={3} />);
      i += 1;
      continue;
    }

    if (line.trim().startsWith("|") && isTableSeparatorRow(lines[i + 1] ?? "")) {
      const header = parseTableRow(line);
      const rows: string[][] = [];
      let j = i + 2;
      while (j < lines.length && lines[j].trim().startsWith("|")) {
        rows.push(parseTableRow(lines[j]));
        j += 1;
      }
      blocks.push(<DeliveryTypeTable key={i} header={header} rows={rows} />);
      i = j;
      continue;
    }

    if (line.trim().startsWith("- ")) {
      blocks.push(
        <Text
          key={i}
          fontWeight="400"
          color="gray.700"
          lineHeight="tall"
          display="flex"
          alignItems="center"
          gap={2}
        >
          {renderInline(line.trim().slice(2))}
        </Text>
      );
      i += 1;
      continue;
    }

    blocks.push(
      <Text key={i} fontWeight="400" color="gray.700" lineHeight="tall">
        {renderInline(line)}
      </Text>
    );
    i += 1;
  }

  return <Box>{blocks}</Box>;
};

export const AanleveringenDetail: FC = () => {
  const { type } = useParams<{ type: string }>();
  const deliveryType = DELIVERY_TYPES.find((entry) => entry.slug === type);

  useEffect(() => {
    if (deliveryType) {
      document.title = `${deliveryType.metaTitle} | BROSTAR`;
      setMetaDescription(deliveryType.metaDescription);
      return () => {
        document.title = "BROSTAR - BRO Grondwatermonitoring Software voor Bronhouders";
        setMetaDescription(
          "BROSTAR is de Nederlandse SaaS-oplossing voor BRO grondwatermonitoring en grondwatergebruik. Beheer GMW, GMN, GLD, GAR, FRD, GUF en GPD via formulieren. Of koppel jou bron-systeem aan de BRO middels een koppeling via BROSTAR. Ideaal voor waterschappen, gemeenten en provincies."
        );
      };
    }
  }, [deliveryType]);

  if (!deliveryType) {
    return <Navigate to="/public/aanleveringen" replace />;
  }

  return (
    <Box>
      <Box bg="accentLight" px={{ base: 6, md: 20 }} py={{ base: 14, md: 20 }}>
        <Box maxW="800px" mx="auto">
          <Text fontSize="sm" fontWeight="700" color="gray.500" mb={3}>
            AANLEVERINGSTYPE
          </Text>
          <Heading as="h1" fontSize={{ base: "2xl", md: "4xl" }} color="text" lineHeight="short">
            {deliveryType.title}
          </Heading>
        </Box>
      </Box>

      <PageSection>
        <Box maxW="800px" mx="auto">
          <Text fontWeight="400" fontSize="lg" color="gray.600" lineHeight="tall" mb={8}>
            {deliveryType.excerpt}
          </Text>
          <Divider mb={8} />
          <DeliveryTypeContent content={deliveryType.content} />
          <Divider mt={12} mb={8} />
          <Link to="/public/aanleveringen" style={{ textDecoration: "none" }}>
            <Text fontSize="sm" fontWeight="700" color="primary">
              ← Terug naar aanleveringen
            </Text>
          </Link>
        </Box>
      </PageSection>
    </Box>
  );
};
